import { reactive } from 'vue'
import platformFeatureService from '@/services/platformFeatureService'

const isEnabled = (value) => value === 'true'

const features = reactive({
  /**
   * Tenant-facing subscription feature: plan catalogue, My Subscription, the sidebar and
   * profile-menu widgets. Switched by a super admin on the Subscription Plans screen.
   */
  subscription: false,

  /**
   * Tenant-facing contracts: My Contracts and the purchase agreement at checkout.
   * Switched by a super admin on the Contract Templates screen.
   */
  contracts: false,

  /**
   * reCAPTCHA v2 site key for the sign-up form. The API returns it only when both the site
   * key and the secret are configured, so the form and the server always agree on whether
   * a challenge is required.
   */
  captchaSiteKey: '',

  /**
   * Supplier registration enforcement: confining unapproved accounts to the flow, sending
   * them there at sign-in, and the reminder banner.
   *
   * Off by default. Turning it on affects only tenants that signed up *after* the flow
   * existed: those have a draft row created at registration, and its absence is what
   * exempts everyone who came before. So this switch does not have to wait for the
   * existing tenants to be migrated — it was written that way on purpose, because a
   * migration that has to happen before a flag can be flipped never happens.
   */
  onboardingEnforcement: isEnabled(import.meta.env.VITE_FEATURE_ONBOARDING_ENFORCEMENT)
})

const apply = (result) => {
  features.subscription = Boolean(result?.subscription)
  features.contracts = Boolean(result?.contracts)
  features.captchaSiteKey = result?.captchaSiteKey || ''
}

let loadPromise = null

/**
 * Reads the runtime switches from the API, once. Everything stays off until the API says
 * otherwise, so a failed or slow request never shows a feature by accident.
 *
 * main.js starts it before mounting, and the router guard awaits it too: the router begins
 * its first navigation as soon as it is installed, before mount, so without the await a
 * reload on a feature route would be judged against the default (off) and bounced.
 */
export function loadFeatureFlags() {
  if (!loadPromise) {
    loadPromise = platformFeatureService.getFeatures()
      .then(apply)
      .catch(() => apply(null))
  }
  return loadPromise
}

/** Super admin only: turns one platform feature ('subscription' | 'contracts') on or off. */
export async function setPlatformFeature(feature, isEnabledValue) {
  apply(await platformFeatureService.updateFeature(feature, isEnabledValue))
  return features[feature]
}

export function useFeatureFlags() {
  return { features }
}
